import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DialogTitle, Box, Modal, Grid, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Button, Input, Loader } from '../../components';
import { NotesWrapper } from './Style/ViewThreatNotesModal.style';
import {
  useCreateNotesMutation,
  useCreateCommentsMutation,
  useNotesValuesQuery,
} from '../../queries/noteComments';
import NoteList from '../../components/NoteList/NoteList';
import BulkExplainSection from '../../components/ThreatExplain/BulkExplainSection';
import {
  BulkExplainSummary,
  insertSummaries,
} from '../../services/threatExplainPersistence';
import {
  SelectedThreatRow,
  buildNotesBulkExplainPayload,
} from './buildNotesBulkExplainPayload';

// Self-contained layout (not the shared ViewThreatNotesModal styles): a bounded
// flex column so the body scrolls and the footer action row stays pinned to the
// bottom — same pattern as the create-threats modal, so the AI section below the
// fold is reached by scrolling toward the Apply button.
const modalShellSx = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '60%',
  bgcolor: 'background.paper',
  borderRadius: '4px',
  boxShadow: 24,
  p: 3,
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const scrollBodySx = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  // Absorbs the -8px left margin of the inner `Grid container spacing={1}` so
  // overflow clipping doesn't cut the first letter of "Threat Notes List".
  px: '8px',
  py: '10px',
};

const footerSx = {
  display: 'flex',
  gap: 2,
  alignItems: 'center',
  justifyContent: 'flex-end',
  pt: 2,
  mt: 1,
  borderTop: '1px solid',
  borderColor: 'divider',
};

const AddCommonNotesModal = ({ open, handleClose, selectedThreats }: any) => {
  const [selectedNoteIds, setSelectedNoteIds] = useState<number[]>([]);
  const [commentText, setCommentText] = useState('');
  const [aiSummary, setAiSummary] = useState<BulkExplainSummary | null>(null);
  const [summaryPersisting, setSummaryPersisting] = useState(false);
  const submittedRef = useRef<{
    notes: boolean;
    comments: boolean;
    ai: boolean;
  }>({
    notes: false,
    comments: false,
    ai: false,
  });

  const { data: notesValues, isLoading: notesValuesLoading } =
    useNotesValuesQuery();
  const createNotesMutation = useCreateNotesMutation();
  const createCommentsMutation = useCreateCommentsMutation();

  // Selected threats already exist, so the AI summary can reference their real
  // IDs directly — no post-creation fan-out as in the bulk-create modals.
  const selectedRows: SelectedThreatRow[] = useMemo(
    () => selectedThreats ?? [],
    [selectedThreats],
  );

  const bulkExplainPayload = useMemo(
    () =>
      buildNotesBulkExplainPayload({
        selectedThreats: selectedRows,
        commentText,
        selectedNoteIds,
        notesCatalog: notesValues ?? [],
      }),
    [selectedRows, commentText, selectedNoteIds, notesValues],
  );

  // Batch-level context for the generation log row: only meaningful when the
  // whole selection shares one protected asset / company; mixed selections log
  // null (mirrors the bulk-create modals).
  const bulkExplainIds = useMemo(() => {
    const assetIds = [
      ...new Set(selectedRows.map(r => r.domainId).filter(Boolean)),
    ];
    const companyIds = [
      ...new Set(selectedRows.map(r => r.companyId).filter(Boolean)),
    ];
    return {
      protectedAssetId: assetIds.length === 1 ? Number(assetIds[0]) : null,
      companyId: companyIds.length === 1 ? Number(companyIds[0]) : null,
    };
  }, [selectedRows]);

  const hasNotes = selectedNoteIds.length > 0;
  const hasComment = commentText.trim() !== '';
  const hasSummary = !!aiSummary;
  const isSubmitting =
    (submittedRef.current.notes && createNotesMutation.isPending) ||
    (submittedRef.current.comments && createCommentsMutation.isPending) ||
    summaryPersisting;

  const closeAndReset = useCallback(() => {
    setSelectedNoteIds([]);
    setCommentText('');
    setAiSummary(null);
    setSummaryPersisting(false);
    submittedRef.current = { notes: false, comments: false, ai: false };
    handleClose();
  }, [handleClose]);

  useEffect(() => {
    const { notes, comments, ai } = submittedRef.current;
    if (!notes && !comments && !ai) return;

    const notesDone = !notes || !createNotesMutation.isPending;
    const commentsDone = !comments || !createCommentsMutation.isPending;
    const aiDone = !ai || !summaryPersisting;

    if (notesDone && commentsDone && aiDone) {
      closeAndReset();
    }
  }, [
    createNotesMutation.isPending,
    createCommentsMutation.isPending,
    summaryPersisting,
    closeAndReset,
  ]);

  const handleApplyNotes = () => {
    if (hasSummary && aiSummary) {
      // Existing threats — persist one summary row per selection right away.
      submittedRef.current.ai = true;
      setSummaryPersisting(true);
      insertSummaries(
        selectedRows.map(t => ({
          threat_id: t.threatId,
          protected_asset_id: t.domainId,
          company_id: t.companyId,
        })),
        aiSummary,
      )
        .catch(err =>
          // Non-fatal: notes/comments still apply; the experiment row is lost.
          console.error(
            '[threat-explain] persist notes-modal summaries failed',
            err,
          ),
        )
        .finally(() => setSummaryPersisting(false));
    }

    if (hasNotes) {
      const noteItems = selectedThreats
        .map((threat: { domainId: number; threatId: number }) =>
          selectedNoteIds.map(noteId => ({
            domain_id: threat.domainId,
            threat_id: threat.threatId,
            note_id: noteId,
          })),
        )
        .flat();

      submittedRef.current.notes = true;
      createNotesMutation.mutate({ items: noteItems });
    }

    if (hasComment) {
      const commentItems = selectedThreats.map(
        (threat: { domainId: number; threatId: number }) => ({
          comments: commentText.trim(),
          domain_id: threat.domainId,
          threat_id: threat.threatId,
          is_public: false,
        }),
      );

      submittedRef.current.comments = true;
      createCommentsMutation.mutate({ items: commentItems });
    }
  };

  const handleNotesChange = (noteId: number) => {
    setSelectedNoteIds((prevSelectedNoteIds: any) =>
      prevSelectedNoteIds.includes(noteId)
        ? prevSelectedNoteIds.filter((id: any) => id !== noteId)
        : [...prevSelectedNoteIds, noteId],
    );
  };

  return (
    <>
      <Modal open={open} onClose={isSubmitting ? undefined : closeAndReset}>
        <Box data-testid="threat-notes-modal" sx={modalShellSx}>
          <DialogTitle sx={{ m: 0, p: 0, pb: 1.5 }}>Notes</DialogTitle>

          <Box sx={scrollBodySx}>
            <Grid container spacing={1}>
              <Typography sx={{ mb: 1, mt: 1 }}>Threat Notes List:</Typography>
              {/* Flow the note list into the single body scroll instead of its
                  own fixed-height scroll, so scrolling reveals the AI section. */}
              <NotesWrapper sx={{ height: 'auto', overflow: 'visible' }}>
                {notesValuesLoading ? (
                  <Box sx={{ m: 12 }}>
                    <Loader size={25} />
                  </Box>
                ) : (
                  <Grid container spacing={{ xs: 1.5 }}>
                    <NoteList
                      handleNotesChange={handleNotesChange}
                      selectedNoteIds={selectedNoteIds}
                      handleApplyNotes={handleApplyNotes}
                    />
                  </Grid>
                )}
              </NotesWrapper>
            </Grid>
            <Typography sx={{ mb: 1, mt: 2 }}>Add a comment:</Typography>
            <Input
              data-testid="bulk-comment-input"
              multiline
              rows={3}
              placeholder="Write a comment to apply to all selected threats..."
              value={commentText}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setCommentText(e.target.value)
              }
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  height: 'auto',
                  padding: '8px 14px',
                },
              }}
            />
            <BulkExplainSection
              source="bulk_notes"
              payload={bulkExplainPayload}
              protectedAssetId={bulkExplainIds.protectedAssetId}
              companyId={bulkExplainIds.companyId}
              onSummaryChange={setAiSummary}
            />
          </Box>

          <Box sx={footerSx}>
            <Button
              disabled={isSubmitting}
              onClick={closeAndReset}
              variant="outlined"
              color="secondary"
              label="Cancel"
            />
            <Button
              data-testid="apply-notes"
              variant="contained"
              startIcon={<CheckCircleIcon />}
              sx={{
                background: 'primary.main',
                minWidth: 90,
                padding: '6px 13px',
              }}
              disabled={
                (!hasNotes && !hasComment && !hasSummary) || isSubmitting
              }
              onClick={handleApplyNotes}
            >
              {isSubmitting ? <Loader size={18} /> : 'Apply'}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default AddCommonNotesModal;
